import { useMemo, useState } from "react";
import {
  Users,
  UserPlus,
  CalendarClock,
  DollarSign,
  CalendarDays,
  MessageSquare,
  Smile,
  Gauge,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import { usePoliciesStats, useInsuredsStats, usePremiumByMonth } from "@/lib/api";
import type { InsuredStatusFilter, InsuredSortField, SortDir } from "@shared/pagination";
import { useAuth } from "@/hooks/use-auth";
import { useAgencyContext } from "@/lib/agencyContext";
import { DateRangeFilter } from "@/components/dashboard/DateRangeFilter";
import { PrimaryStatCard } from "@/components/dashboard/PrimaryStatCard";
import { SecondaryStatCard } from "@/components/dashboard/SecondaryStatCard";
import { TeamPerformance } from "@/components/dashboard/TeamPerformance";
import { BookOverview, type BookSegment } from "@/components/dashboard/BookOverview";
import { PremiumGrowthChart } from "@/components/dashboard/PremiumGrowthChart";
import { InsuredsListDialog } from "@/components/dashboard/InsuredsListDialog";
import {
  formatCurrencyCompact,
  type DateRangeKey,
} from "@/components/dashboard/dashboard-helpers";

type DialogKind =
  | "activeCustomers"
  | "newCustomers"
  | "renewingCustomers"
  | "premiumContributors"
  | "stableClients"
  | "growingAccounts"
  | "highNeedClients"
  | "atRiskOfChurn"
  | null;

interface DialogPayload {
  title: string;
  description: string;
  status: InsuredStatusFilter | null;
  emptyMessage?: string;
  // CSR-674 R-002: dialog payloads can pin a sort. Used by the Premium
  // Contributors card to surface highest-premium insureds first instead
  // of the server's default (MIN(effective_date) DESC NULLS LAST).
  sort?: InsuredSortField;
  sortDir?: SortDir;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState<DateRangeKey>("thisMonth");
  const [dialog, setDialog] = useState<DialogKind>(null);

  // CSR-674: dashboard no longer loads bulk policies/insureds. Cards
  // pull from the dedicated stats endpoints, the chart from
  // /api/policies/premium-by-month, and the InsuredsListDialog fetches
  // its own paginated data when opened.
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;

  const { data: policiesStats } = usePoliciesStats(agencyId);
  const { data: insuredsStats } = useInsuredsStats(agencyId);
  const { data: premiumSeries = [] } = usePremiumByMonth(agencyId);

  const activeCustomersCount = insuredsStats?.inForce.count ?? 0;
  const renewingCustomersCount = insuredsStats?.expiring.count ?? 0;
  const activePremium = policiesStats?.inForce.premium ?? 0;
  const totalPremium = activePremium;
  const renewingPremium = policiesStats?.expiring.premium ?? 0;

  const welcomeName = useMemo(() => {
    if (user?.firstName) return user.firstName;
    if (user?.email) return user.email.split("@")[0];
    return "there";
  }, [user?.firstName, user?.email]);

  const bookSegments: BookSegment[] = useMemo(
    () => [
      {
        key: "stableClients",
        label: "Stable Clients",
        value: activeCustomersCount,
        total: Math.max(activeCustomersCount, 1),
        description: "Smooth running, low contact, routine requests",
        color: "green",
      },
      {
        key: "growingAccounts",
        label: "Growing Accounts",
        value: 0,
        total: Math.max(activeCustomersCount, 1),
        description: "Mid-term changes, adding vehicles/drivers",
        color: "orange",
      },
      {
        key: "highNeedClients",
        label: "High-Need Clients",
        value: 0,
        total: Math.max(activeCustomersCount, 1),
        description: "High contact volume, repeated issues",
        color: "yellow",
      },
      {
        key: "atRiskOfChurn",
        label: "At Risk of Churn",
        value: 0,
        total: Math.max(activeCustomersCount, 1),
        description: "Dissatisfied, lapsing, or disengaged",
        color: "red",
      },
    ],
    [activeCustomersCount]
  );

  const dialogPayload = useMemo<DialogPayload | null>(() => {
    switch (dialog) {
      case "activeCustomers":
        return {
          title: "Active Customers",
          description: `${activeCustomersCount} insureds with active policies`,
          status: "inForce",
        };
      case "newCustomers":
        return {
          title: "New Customers",
          description: "Insureds in New Customer stage",
          status: null,
          emptyMessage: "New customer tracking isn't wired up in this project yet.",
        };
      case "renewingCustomers":
        return {
          title: "Renewing Customers",
          description: `${renewingCustomersCount} insureds in Renewal stage`,
          status: "expiring",
        };
      case "premiumContributors":
        // CSR-674 R-002: top in-force premium contributors first. Pre-CSR-674
        // this dialog rendered `sortByPremiumDesc(activeCustomers)`; the
        // server `sort=premium&sortDir=desc` recovers that ordering against
        // the augmented insureds row's `activePremium` field.
        return {
          title: "Premium Contributors",
          description: "Active insureds, top premium first",
          status: "inForce",
          sort: "premium",
          sortDir: "desc",
        };
      case "stableClients":
        return {
          title: "Stable Clients",
          description: `${activeCustomersCount} insureds — Smooth running, low contact, routine requests`,
          status: "inForce",
        };
      case "growingAccounts":
        return {
          title: "Growing Accounts",
          description: "Mid-term changes, adding vehicles/drivers",
          status: null,
          emptyMessage: "Client segmentation isn't tracked in this project yet.",
        };
      case "highNeedClients":
        return {
          title: "High-Need Clients",
          description: "High contact volume, repeated issues",
          status: null,
          emptyMessage: "Client segmentation isn't tracked in this project yet.",
        };
      case "atRiskOfChurn":
        return {
          title: "At Risk of Churn",
          description: "Dissatisfied, lapsing, or disengaged",
          status: null,
          emptyMessage: "Client segmentation isn't tracked in this project yet.",
        };
      default:
        return null;
    }
  }, [dialog, activeCustomersCount, renewingCustomersCount]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Welcome back, {welcomeName}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Here's your agency overview for today.
            </p>
          </div>
          <DateRangeFilter active={dateRange} onChange={setDateRange} />
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PrimaryStatCard
            icon={Users}
            iconClass="bg-amber-100 text-amber-700"
            label="Active Customers"
            value={String(activeCustomersCount)}
            subtitle={formatCurrencyCompact(activePremium)}
            onClick={() => setDialog("activeCustomers")}
            testId="stat-active-customers"
          />
          <PrimaryStatCard
            icon={UserPlus}
            iconClass="bg-emerald-100 text-emerald-700"
            label="New Customers"
            value="—"
            subtitle="—"
            onClick={() => setDialog("newCustomers")}
            testId="stat-new-customers"
          />
          <PrimaryStatCard
            icon={CalendarClock}
            iconClass="bg-orange-100 text-orange-700"
            label="Renewing Customers"
            value={String(renewingCustomersCount)}
            subtitle={formatCurrencyCompact(renewingPremium)}
            onClick={() => setDialog("renewingCustomers")}
            testId="stat-renewing-customers"
          />
          <PrimaryStatCard
            icon={DollarSign}
            iconClass="bg-violet-100 text-violet-700"
            label="Total Premium"
            value={formatCurrencyCompact(totalPremium)}
            onClick={() => setDialog("premiumContributors")}
            testId="stat-total-premium"
          />
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SecondaryStatCard
            icon={CalendarDays}
            iconClass="bg-violet-100 text-violet-700"
            label="Avg. Tenure"
            value="—"
            testId="stat-avg-tenure"
          />
          <SecondaryStatCard
            icon={MessageSquare}
            iconClass="bg-sky-100 text-sky-700"
            label="Automated Convos"
            value="—"
            testId="stat-automated-convos"
          />
          <SecondaryStatCard
            icon={Smile}
            iconClass="bg-amber-100 text-amber-700"
            label="CSAT"
            value="—"
            testId="stat-csat"
          />
          <SecondaryStatCard
            icon={Gauge}
            iconClass="bg-teal-100 text-teal-700"
            label="NPS"
            value="—"
            testId="stat-nps"
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <TeamPerformance
              metrics={[
                { label: "First Response", value: "—" },
                { label: "Conversations", value: "—" },
                { label: "Unique Insureds", value: "—" },
                { label: "Handle Time", value: "—" },
                { label: "Avg Days Between", value: "—" },
                { label: "Avg Conv/Contact", value: "—" },
              ]}
              topTeammates={[]}
            />
          </div>
          <BookOverview
            segments={bookSegments}
            onSegmentClick={(key) => setDialog(key as DialogKind)}
          />
        </section>

        <section>
          <PremiumGrowthChart data={premiumSeries} />
        </section>
      </div>

      <InsuredsListDialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        title={dialogPayload?.title ?? ""}
        description={dialogPayload?.description ?? ""}
        status={dialogPayload?.status ?? null}
        agencyId={agencyId}
        emptyMessage={dialogPayload?.emptyMessage}
        sort={dialogPayload?.sort}
        sortDir={dialogPayload?.sortDir}
      />
    </Layout>
  );
}
